<template>
    <slot name="stone-type"></slot>

    <FilterAccordion
        v-qa="'filter-price'"
        :count="
            modelValue?.priceRange?.min?.amount &&
            modelValue?.priceRange?.max?.amount
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.price.input')"
        hide-count
    >
        <slot name="price"></slot>
    </FilterAccordion>

    <FilterAccordion
        :count="
            modelValue?.caratWeightRange?.min ||
            modelValue?.caratWeightRange?.max
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.carat_size.input')"
        hide-count
    >
        <template #default="{ disabled: disabledAccordion }">
            <FilterRange
                v-model="caratWeightModel"
                v-qa="'filter-carat-size'"
                :bounds="bounds.caratWeight"
                :disabled="disabledAccordion"
                :step="0.05"
                :value-formatter="(value: number) => value.toFixed(2)"
                :min-label="'Minimum carat size'"
                :max-label="'Maximum carat size'"
            />
        </template>
    </FilterAccordion>

    <FilterAccordion
        :count="typesModel.length"
        :title="t('diamond_search.filter.gemstone_type.input')"
    >
        <template #default="{ disabled: disabledAccordion }">
            <GemstoneTypeFilter
                v-model="typesModel"
                v-qa="'filter-gemstone-type'"
                :bounds="bounds.types"
                :disabled="disabledAccordion"
                is-multi
            />
        </template>
    </FilterAccordion>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
    NonSkuCaratWeightRangeInput,
    NonSkuGemstoneBoundsFragment,
    NonSkuGemstoneFilters,
    NonSkuGemstoneType,
} from '@/graphql/api'

import FilterRange from '@/components/stone/search/nonsku/filter/FilterRange.vue'
import GemstoneTypeFilter from '@/components/stone/search/nonsku/filter/GemstoneTypeFilter.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'

const props = defineProps<{
    modelValue: NonSkuGemstoneFilters | null | undefined
    bounds: NonSkuGemstoneBoundsFragment
}>()

const emit = defineEmits<{
    'update:modelValue': [v: NonSkuGemstoneFilters]
}>()

const { t } = useI18n()

const caratWeightModel = computed({
    get(): NonSkuCaratWeightRangeInput | null | undefined {
        const values = props.modelValue

        if (!values) {
            return props.bounds.caratWeight
        }

        if (!values.caratWeightRange) {
            return props.bounds.caratWeight
        }

        return values.caratWeightRange
    },
    set(range: NonSkuCaratWeightRangeInput | null | undefined) {
        emit('update:modelValue', {
            ...props.modelValue,
            caratWeightRange: range,
        })
    },
})

const typesModel = computed({
    get: (): NonSkuGemstoneType[] =>
        props.bounds.types.filter((t) => {
            const values = props.modelValue

            if (!values) {
                return false
            }

            return values.types?.includes(t.id)
        }),
    set: (value: NonSkuGemstoneType[]) =>
        emit('update:modelValue', {
            ...props.modelValue,
            types: value.map((t) => t.id),
        }),
})
</script>
